// src/components/ItemSharePage.js - Fixed to preserve item for cart after authentication
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { Store } from "@mui/icons-material";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import ItemDetailsModal from "./ItemDetailsModal";
import { brandPalette as primaryBlue } from "../config/brandTheme";

const ItemSharePage = () => {
  const { itemId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [showCTA, setShowCTA] = useState(false);

  // EXACT same styles as HomePage

  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  useEffect(() => {
    fetchItemDetails();
  }, [itemId]);

  // Show CTA when modal closes
  useEffect(() => {
    if (!modalOpen && item && !loading) {
      const timer = setTimeout(() => {
        setShowCTA(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [modalOpen, item, loading]);

  const fetchItemDetails = async () => {
    try {
      setLoading(true);

      // Try to fetch item from Firestore by document ID
      const itemRef = doc(db, "items", itemId);
      const itemSnapshot = await getDoc(itemRef);

      if (itemSnapshot.exists()) {
        const itemData = itemSnapshot.data();
        const fullItem = {
          id: itemSnapshot.id,
          ...itemData,
        };

        console.log("=== DEBUG SHARED ITEM ===");
        console.log("Full item data:", fullItem);
        console.log("Item has sizes:", fullItem.sizes);
        console.log("Item has colors:", fullItem.colors);
        console.log("Item description:", fullItem.description);
        console.log("Item stock:", fullItem.stockQuantity);
        console.log("========================");

        setItem(fullItem);
        // Auto-open modal when item is loaded
        setModalOpen(true);
      } else {
        // Fallback to URL parameters if item not found in DB
        const itemName = searchParams.get("name");
        const itemPrice = searchParams.get("price");

        if (itemName && itemPrice) {
          const fallbackItem = {
            id: itemId,
            name: itemName,
            sellingPrice: parseFloat(itemPrice),
            description: "Full product details available in our store",
            category: "Product",
            stockQuantity: 1,
            minOrderQuantity: 1,
            maxOrderQuantity: 10,
            imageUrl: "/api/placeholder/400/400", // Add placeholder image
          };
          setItem(fallbackItem);
          setModalOpen(true);
        } else {
          setError("Item not found");
        }
      }
    } catch (error) {
      console.error("Error fetching item:", error);
      setError("Failed to load item details");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const handleViewFullStore = () => {
    navigate("/");
  };

  // FIXED: Store item in sessionStorage and navigate with intent
  const addItemToCart = (item, quantity = 1, selectedVariants = {}) => {
    console.log("🛒 ITEM SHARE - Add to cart clicked", {
      item,
      quantity,
      selectedVariants,
    });

    // Store the item details in sessionStorage for retrieval after authentication
    const pendingCartItem = {
      type: "item",
      id: item.id,
      name: item.name,
      sellingPrice: item.sellingPrice,
      description: item.description,
      category: item.category,
      stockQuantity: item.stockQuantity,
      minOrderQuantity: item.minOrderQuantity,
      maxOrderQuantity: item.maxOrderQuantity,
      imageUrl: item.imageUrl,
      sizes: item.sizes,
      colors: item.colors,
      quantity: quantity,
      selectedVariants: selectedVariants,
      addedFromShare: true,
      shareTimestamp: Date.now(),
    };

    sessionStorage.setItem("pendingCartItem", JSON.stringify(pendingCartItem));

    // Navigate to home with authentication intent
    console.log("🔄 Navigating to home with authentication intent");
    navigate("/?auth_intent=add_to_cart&item_id=" + item.id);
  };

  const setSelectedItem = () => {};
  const setShowItemCheckout = () => {};
  const getItemCartQuantity = () => 0;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: `linear-gradient(135deg, 
            ${primaryBlue[50]} 0%, 
            ${primaryBlue[100]} 25%, 
            ${primaryBlue[200]} 50%, 
            ${primaryBlue[100]} 75%, 
            ${primaryBlue[50]} 100%)`,
        }}
      >
        <Box sx={{ textAlign: "center", ...glassStyles, p: 4 }}>
          <CircularProgress sx={{ color: primaryBlue[600], mb: 2 }} />
          <Typography variant="h6" sx={{ color: primaryBlue[700] }}>
            Loading product details...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !item) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: `linear-gradient(135deg, 
            ${primaryBlue[50]} 0%, 
            ${primaryBlue[100]} 25%, 
            ${primaryBlue[200]} 50%, 
            ${primaryBlue[100]} 75%, 
            ${primaryBlue[50]} 100%)`,
        }}
      >
        <Container maxWidth="sm">
          <Alert severity="error" sx={{ ...glassStyles, mb: 3 }}>
            <Typography variant="h6">{error || "Item not found"}</Typography>
            <Typography variant="body2">
              The item you're looking for might have been removed or the link is
              invalid.
            </Typography>
          </Alert>
          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleViewFullStore}
              startIcon={<Store />}
              sx={{
                background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[500]})`,
                px: 4,
                py: 2,
              }}
            >
              Browse All Products
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, 
          ${primaryBlue[50]} 0%, 
          ${primaryBlue[100]} 25%, 
          ${primaryBlue[200]} 50%, 
          ${primaryBlue[100]} 75%, 
          ${primaryBlue[50]} 100%)`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      {/* Show call-to-action when modal is closed */}
      {showCTA && !modalOpen && (
        <Container
          maxWidth="lg"
          sx={{ py: 8, position: "relative", zIndex: 1 }}
        >
          <Box sx={{ ...glassStyles, p: 6, textAlign: "center" }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{ color: primaryBlue[800], fontWeight: 700 }}
            >
              🛍️ InfinityWhale
            </Typography>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ color: primaryBlue[700], fontWeight: 600 }}
            >
              Loved "{item.name}"?
            </Typography>
            <Typography variant="h6" sx={{ color: primaryBlue[600], mb: 4 }}>
              Discover thousands more amazing products in our store!
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleViewFullStore}
              sx={{
                px: 6,
                py: 2,
                background: `linear-gradient(45deg, #00BFFF, #60A5FA)`,
                fontWeight: "bold",
                fontSize: "1.2rem",
                "&:hover": {
                  background: `linear-gradient(45deg, #388e3c, #00BFFF)`,
                  transform: "translateY(-2px)",
                },
              }}
            >
              🚀 Shop Now at InfinityWhale
            </Button>
          </Box>
        </Container>
      )}

      {/* ItemDetailsModal - With FIXED addItemToCart function */}
      <ItemDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        item={item}
        formatPrice={formatPrice}
        addItemToCart={addItemToCart}
        setSelectedItem={setSelectedItem}
        setShowItemCheckout={setShowItemCheckout}
        getItemCartQuantity={getItemCartQuantity}
        primaryBlue={primaryBlue}
        glassStyles={glassStyles}
      />
    </Box>
  );
};

export default ItemSharePage;
